<template>
  <div>
    <!-- Header Info -->
    <b-row class="mb-3">
      <b-col md="3">
        <div class="mb-3">
          <span class="d-block text-muted">IMEI</span>
          <strong>{{ formData.imei }}</strong>
        </div>
      </b-col>

      <b-col md="2">
        <div class="mb-3">
          <span class="d-block text-muted">Vendor</span>
          <strong>{{ formData.vendorType }}</strong>
        </div>
      </b-col>

      <b-col md="2">
        <div class="mb-3">
          <span class="d-block text-muted">Device</span>
          <strong>{{ formData.deviceName }}</strong>
        </div>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <div v-if="isInitializing" class="text-center text-muted mb-3">
      <b-spinner small class="mr-2"></b-spinner>
      Loading form data...
    </div>

    <!-- Data Not Found Alert -->
    <div v-if="!isInitializing && !hasValidData" class="alert alert-warning" role="alert">
      <strong>Peringatan:</strong> Data kendaraan tidak ditemukan. Form telah dinonaktifkan.
    </div>

    <!-- FORM -->
    <FormulateForm
      v-model="formData"
      @submit="handleSubmit"
      :errors="formErrors"
      @failed-validation="handleFailedValidation"
    >
      <div class="mb-2">
        <label class="required">{{ $t('setting.modal.targetCompany') }}</label>
        <multiselect
          v-model="formData.companies"
          :options="companies"
          track-by="company_id"
          placeholder="Pilih Company"
          :custom-label="customLabelCompany"
          :disabled="true"
          :class="{
            'is-invalid': $v.value.companies.$error,
          }"
        />
        <div v-if="$v.value.companies.$error" class="invalid-feedback mt-0">
          <span v-if="!$v.value.companies.required">
            {{ $t('setting.modal.targetCompany') }} Wajib Terisi
          </span>
        </div>
      </div>

      <div class="mb-2">
        <label class="required" for="newLicensePlate">
          {{ $t('setting.modal.newLicensePlate') }}
        </label>
        <multiselect
          :disabled="!hasValidData || isInitializing || disabledSD"
          v-model="formData.selectedVehicle"
          :options="vehicles"
          label="license_plate"
          track-by="vehicle_id"
          placeholder="Pilih Kendaraan"
          @input="onVehicleSelect"
          :taggable="true"
          @tag="addVehicleTag"
          :class="{
            'is-invalid': $v.value.selectedVehicle.$error,
          }"
        />
        <div v-if="$v.value.selectedVehicle.$error" class="invalid-feedback mt-0">
          <span v-if="!$v.value.selectedVehicle.required">
            {{ $t('setting.modal.newLicensePlate') }} Wajib Terisi
          </span>
        </div>
      </div>

      <div class="mb-2">
        <label class="required" for="vehicleType">{{ $t('setting.modal.vehicleType') }}</label>
        <multiselect
          :disabled="!hasValidData || isInitializing || disabledSD"
          v-model="formData.selectedVehicleType"
          :options="vehicleTypes"
          :custom-label="vehicleTypeLabel"
          track-by="vehicle_type_id"
          placeholder="Pilih Jenis Kendaraan"
          :class="{
            'is-invalid': $v.value.selectedVehicleType.$error,
          }"
        />
        <div v-if="$v.value.selectedVehicleType.$error" class="invalid-feedback mt-0">
          <span v-if="!$v.value.selectedVehicleType.required">
            {{ $t('setting.modal.vehicleType') }} Wajib Terisi
          </span>
        </div>
      </div>

      <div class="mb-2">
        <label class="required" for="vehicleType">{{ $t('setting.modal.newSimcard') }}</label>
        <FormulateInput
          type="number"
          v-model="formData.phoneNo"
          name="phoneNo"
          placeholder="Ganti dengan Simcard baru"
          :validation="['required', 'phone']"
          :validation-messages="{
            required: `${$t('setting.modal.newSimcard')} Wajib Terisi`,
            phone: `${$t('qcdevice.incomming.invalidPhoneNumber')}`,
          }"
          validation-behavior="live"
          error-behavior="live"
          @keydown="portKeydown($event, (preventZeroFirst = true))"
          :disabled="true"
        />
      </div>

      <div class="mb-2">
        <label class="required" for="vehicleType">Odometer</label>
        <FormulateInput
          :disabled="!hasValidData || isInitializing || disabledSD"
          type="number"
          v-model="formData.odometer"
          name="odometer"
          placeholder="Masukkan Odometer (Km)"
          validation-behavior="live"
          error-behavior="live"
          :validation="['required']"
          :validation-messages="{
            required: `Odometer Wajib Terisi`,
          }"
          mt="2"
          @keydown="portKeydown($event)"
        />
      </div>

      <FormulateInput
        :disabled="!hasValidData || isInitializing || disabledSD"
        type="number"
        v-model="formData.vehicleYear"
        name="vehicleYear"
        label="Tahun Kendaraan"
        placeholder="Masukkan Tahun Kendaraan"
        mt="2"
        @keydown="portKeydown($event)"
      />

      <FormulateInput
        :disabled="!hasValidData || isInitializing || disabledSD"
        type="text"
        v-model="formData.hullNo"
        name="hullNo"
        label="Nomor Lambung"
        placeholder="Masukkan Nomor Lambung"
        mt="2"
      />
    </FormulateForm>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { required } from 'vuelidate/lib/validators'
import * as vehicleApi from '@/services/rest-api-sms/lib/vehicle/vehicle'
import * as vehicleTypeApi from '@/services/rest-api-sms/lib/vehicle/vehicle-type'
import * as companyApi from '@/services/rest-api-sms/lib/company/company'
import * as deviceApi from '@/services/rest-api-sms/lib/device/device'
import { Helper } from '@/mixin/helper'

export default {
  name: 'FormDeviceSetToCustomer',

  components: { Multiselect },

  mixins: [Helper],

  props: {
    value: Object,
    isEdit: Boolean,
    disabledSD: Boolean,
    taskData: Object,
  },

  data() {
    return {
      vehicles: [],
      vehicleTypes: [],
      formErrors: {},
      companies: [],
      isLoading: false,
      isInitializing: true,
      hasValidData: false,
      resolvedVehicleId: null,
      initSteps: {
        companiesFetched: false,
        vehicleTypesFetched: false,
        taskInitialized: false,
        dataValidated: false,
      },
    }
  },

  validations: {
    value: {
      companies: { required },
      selectedVehicle: { required },
      selectedVehicleType: { required },
      phoneNo: { required },
      odometer: { required },
    },
  },

  computed: {
    formData: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },

  watch: {
    'formData.companies': {
      handler(newCompany) {
        if (newCompany) {
          this.onCompanySelect(newCompany)
        }
      },
      immediate: false,
    },
  },

  async mounted() {
    try {
      this.isInitializing = true
      await this.fetchCompany()
      this.initSteps.companiesFetched = true
      await this.fetchVehicleTypes()
      this.initSteps.vehicleTypesFetched = true
      await this.initializeFromTask()
      this.initSteps.taskInitialized = true
      this.hasValidData = this.validateInitializedData()
      this.initSteps.dataValidated = true

      // ✅ STEP 5: Touch validation
      this.$v.value.$touch()
    } catch (error) {
      console.error('🔴 [SD-INIT-ERROR] Initialization failed:', error)
      this.hasValidData = false
    } finally {
      this.$nextTick(() => {
        this.isInitializing = false
      })
    }
  },

  methods: {
    vehicleTypeLabel(option) {
      if (!option) return ''
      if (option.vehicle_type_name) return option.vehicle_type_name
      return `${option.manufacturer} - ${option.model} ${option.type}`
    },

    /**
     * ✅ FIX: Remove imei is null filter - it breaks SD context
     * In SD step, IMEI is already assigned from RCD step
     */
    async onCompanySelect(company) {
      if (!company) return
      try {
        let params = {
          // ✅ FIXED: Remove 'imei is null' filter
          // SD step needs vehicles regardless of IMEI status
          filters: [`company_id eq ${company.company_id}`],
          limit: 1,
        }
        let response = await vehicleApi.get(params, this.taskData?.server_region || 'SG')
        if (response.status && response.metadata.total_count > 1) {
          params.limit = response.metadata.total_count
          response = await vehicleApi.get(params, this.taskData?.server_region || 'SG')
        }
        this.vehicles = response.data?.paginated_result || []
      } catch (error) {
        console.error('Error fetching vehicles by company:', error)
        this.vehicles = []
      }
    },

    async fetchVehicleTypes() {
      try {
        const region = this.taskData?.server_region || 'SG'
        const params = { limit: 1, page: 1 }
        const response = await vehicleTypeApi.get(params, region)
        if (response.status && response.metadata.total_count > 1) {
          params.limit = response.metadata.total_count
          const fullResponse = await vehicleTypeApi.get(params, region)
          this.vehicleTypes = fullResponse.data?.paginated_result || []
        } else {
          this.vehicleTypes = response.data?.paginated_result || []
        }
      } catch (error) {
        console.error('Error fetching vehicle types:', error)
        this.vehicleTypes = []
      }
    },

    async onVehicleSelect(vehicle) {
      if (!this.vehicleTypes.length) {
        await this.fetchVehicleTypes()
      }

      if (!vehicle) return

      if (vehicle.odometer) {
        this.formData.odometer = vehicle.odometer / 1000
      }
      if (vehicle.hull_no) {
        this.formData.hullNo = vehicle.hull_no
      }
      if (vehicle.vehicle_type_id) {
        const foundType = this.vehicleTypes.find(
          (t) => t.vehicle_type_id === vehicle.vehicle_type_id
        )
        if (foundType) {
          this.formData.selectedVehicleType = foundType
        }
      }
    },

    async addVehicleTag(newValue) {
      const tag = {
        license_plate: newValue,
        vehicle_id: this.resolvedVehicleId,
        vehicle_type_id: this.formData.selectedVehicleType?.vehicle_type_id || 0,
        odometer: this.formData.odometer || 0,
        hull_no: this.formData.hullNo || '',
      }
      this.formData.selectedVehicle = tag
      this.vehicles.push(tag)
    },

    async validateForm() {
      this.$v.value.$touch()
      return !this.$v.value.$error
    },

    async handleSubmit() {
      await this.validateForm()
    },

    customLabelCompany(item) {
      return `${item.name} (${item.domain_name})`
    },

    handleFailedValidation() {
      return
    },

    async fetchCompany() {
      try {
        this.isLoading = true
        const region = this.taskData?.server_region || 'SG'
        let params = { limit: 1, page: 1 }
        let response = await companyApi.get(params, region)
        if (response.status && response.metadata.total_count > 1) {
          params.limit = response.metadata.total_count
          response = await companyApi.get(params, region)
        }
        this.companies = response.data?.paginated_result || []
        this.isLoading = false
      } catch (error) {
        console.error('Error loading companies:', error)
        this.companies = []
        this.isLoading = false
      }
    },

    async fetchVehicleByPlateAndCompany(licensePlate, companyId) {
      if (!licensePlate || !companyId) return null
      try {
        const region = this.taskData?.server_region || 'SG'
        const params = {
          filters: [
            `license_plate eq '${licensePlate}'`,
            `company_id eq ${companyId}`,
            `imei is null`, // ← syarat IMEI belum terisi
          ],
          limit: 1,
        }
        const response = await vehicleApi.get(params, region)
        if (response.status && response.data?.paginated_result?.length) {
          const vehicle = response.data.paginated_result[0]
          console.log('✅ Found vehicle with no IMEI:', vehicle.vehicle_id)
          return vehicle
        }
        return null
      } catch (error) {
        console.error('Error fetching vehicle by plate & company:', error)
        return null
      }
    },

    async resolveVehicleId() {
      // 1. Jika taskData sudah punya vehicle_id
      if (this.taskData?.vehicle_id && this.taskData.vehicle_id !== 0) {
        console.log('✅ Vehicle ID from taskData:', this.taskData.vehicle_id)
        this.resolvedVehicleId = this.taskData.vehicle_id
        return this.taskData.vehicle_id
      }

      // 3. 🔥 TAMBAHAN: Cari berdasarkan license_plate + company + imei null
      if (this.taskData?.license_plate && this.taskData?.company_id) {
        const vehicle = await this.fetchVehicleByPlateAndCompany(
          this.taskData.license_plate,
          this.taskData.company_id
        )
        if (vehicle) {
          this.resolvedVehicleId = vehicle.vehicle_id
          // ⚠️ Opsional: simpan juga data lain jika perlu
          this.taskData.vehicle_id = vehicle.vehicle_id
          return vehicle.vehicle_id
        }
      }

      // 2. Coba cari dari IMEI (via device)
      if (this.taskData?.imei) {
        try {
          const region = this.taskData?.server_region || 'SG'
          const deviceResponse = await deviceApi.getByImei(this.taskData.imei, region)
          if (deviceResponse?.status && deviceResponse.data) {
            const vehicleId =
              deviceResponse.data?.vehicle_id || deviceResponse.data?.vehicle?.vehicle_id
            if (vehicleId && vehicleId !== 0) {
              console.log('✅ Vehicle ID from deviceApi:', vehicleId)
              this.resolvedVehicleId = vehicleId
              return vehicleId
            }
          }
        } catch (error) {
          console.warn('⚠️ Error fetching device by IMEI:', error)
        }
      }

      console.warn('❌ Could not resolve vehicle_id')
      this.resolvedVehicleId = null
      return null
    },

    validateInitializedData() {
      const validation = {
        hasCompany: !!this.formData.companies?.company_id,
        hasVehicle: !!this.formData.selectedVehicle?.vehicle_id,
        hasVehicleType: !!this.formData.selectedVehicleType?.vehicle_type_id,
        hasPhone: !!this.formData.phoneNo,
      }

      return !!(
        validation.hasCompany &&
        validation.hasVehicle &&
        validation.hasVehicleType &&
        validation.hasPhone
      )
    },

    async initializeFromTask() {
      if (!this.taskData) {
        console.warn('❌ No taskData provided')
        return
      }

      try {
        // ✅ PRE-CHECK: Ensure lookups exist
        if (!this.companies.length) {
          console.warn('⚠️ Companies empty, re-fetching...')
          await this.fetchCompany()
        }
        if (!this.vehicleTypes.length) {
          console.warn('⚠️ Vehicle types empty, re-fetching...')
          await this.fetchVehicleTypes()
        }

        // --- STEP 1: Set Company ---
        let company = this.companies.find((c) => c.company_id === this.taskData.company_id)
        if (company) {
          this.formData.companies = company
          await this.onCompanySelect(company)
        } else {
          console.warn('❌ Company not found:', this.taskData.company_id)
          return
        }

        // --- STEP 2: Resolve Vehicle ID ---
        const vehicleId = await this.resolveVehicleId()
        if (vehicleId === null || vehicleId === undefined) {
          console.warn('❌ Could not resolve vehicle_id')
          return
        }

        // --- STEP 3: Construct vehicle data ---
        let vehicleData = {
          vehicle_id: vehicleId,
          license_plate: this.taskData.license_plate,
          odometer: this.taskData.odometer || 0,
          hull_no: this.taskData.hull_no || '',
          year: this.taskData.vehicle_year || null,
          vehicle_type_id: this.taskData.vehicle_type_id || null,
        }

        // --- STEP 4: Find or create vehicle in options ---
        let selectedVehicle = this.vehicles.find((v) => v.vehicle_id === vehicleData.vehicle_id)

        // ✅ FIX: If not found in API results, create from taskData (fallback)
        if (!selectedVehicle) {
          selectedVehicle = {
            vehicle_id: vehicleId,
            license_plate: vehicleData.license_plate,
            odometer: vehicleData.odometer,
            hull_no: vehicleData.hull_no,
            year: vehicleData.year,
            vehicle_type_id: vehicleData.vehicle_type_id,
          }
          this.vehicles.push(selectedVehicle)
        } else {
          console.log('✅ Vehicle found in API list:', selectedVehicle.license_plate)
        }

        // Set selected vehicle
        this.formData.selectedVehicle = selectedVehicle
        const vehicleTypeId = vehicleData.vehicle_type_id || this.taskData.vehicle_type_id
        if (vehicleTypeId) {
          let vt = this.vehicleTypes.find((t) => t.vehicle_type_id === vehicleTypeId)
          if (vt) {
            this.formData.selectedVehicleType = vt
          } else {
            console.warn('❌ Vehicle type not found:', vehicleTypeId)
          }
        }

        // --- STEP 6: Set Odometer, Hull, Year ---
        if (vehicleData.odometer) {
          this.formData.odometer = vehicleData.odometer / 1000
        } else if (this.taskData.odometer) {
          this.formData.odometer = this.taskData.odometer / 1000
        }

        if (vehicleData.hull_no) {
          this.formData.hullNo = vehicleData.hull_no
        } else if (this.taskData.hull_no) {
          this.formData.hullNo = this.taskData.hull_no
        }

        if (vehicleData.year) {
          this.formData.vehicleYear = vehicleData.year
        } else if (this.taskData.vehicle_year) {
          this.formData.vehicleYear = this.taskData.vehicle_year
        }

        // --- STEP 7: Set Phone Number ---
        const phoneNo =
          this.taskData.phone_no || this.taskData.phoneNo || this.taskData.phone_number
        if (phoneNo) {
          this.formData.phoneNo = phoneNo
        } else {
          console.warn('❌ Phone number not found in taskData')
        }
      } catch (error) {
        console.error('🔴 Error during form initialization:', error)
      }
    },
  },
}
</script>
